import {Offer as SapiOffer} from '@findhotel/sapi/dist/types/packages/core/src/types'
import {Offer} from 'offer/types/offer'
import {toggle} from 'opticks/lib/optimizely'
import {allPass, any, curry, equals, head, includes, not, pipe} from 'ramda'
import {safelyGetStorageItem} from '../../utils/persistence'
import {AugmentedOffer} from '../types/AugmentedOffer'
import {
  IsLockedDealType,
  IsPrivateDealType,
  PrivilegedUserTokenType
} from '../types/PrivateDeals'
import isBofhOffer from './isBofhOffer'
import {isEpsProvider} from './isEpsProvider'

import {SearchParamsType} from 'types/Search'
import {
  getCognitoUserTierFromCookie,
  getUserTierFromCookie
} from '../../auth/business/persistData'
import {hasPlusTier} from '../../auth/modules/selectors'

export const DEFAULT_NATIVE_PRIVATE_DEAL_PROVIDER_CODE = 'FHT'

export const NONE_TYPE = 'none'
export const OFFLINE_CUG_TYPE = 'offline'
export const SIGNED_IN_CUG_TYPE = 'signed_in'
export const SENSITIVE_CUG_TYPE = 'sensitive'
export const PLUS_CUG_TYPE = 'prime'
export const FRIENDS_AND_FAMILY_CUG_TYPE = 'fsf'
export const SIGN_UP_TOKEN = 'lkjs82'
export const GSITE_VALUE_REGEX = /\S*ific\S*/gi

export enum USER_TIERS {
  employee = 'employee',
  plus = 'plus',
  friend = 'friend'
}

export const PLUS_TIERS = [USER_TIERS.employee, USER_TIERS.plus]

export enum OFFER_RESTRICTION_TYPES {
  UNRESTRICTED = 'UNRESTRICTED',
  LOCKED_NATIVE_PRIVATE = 'LOCKED_NATIVE_PRIVATE',
  LOCKED_ENGINEERED_PRIVATE = 'LOCKED_ENGINEERED_PRIVATE',
  UNLOCKED_NATIVE_PRIVATE = 'UNLOCKED_NATIVE_PRIVATE',
  UNLOCKED_ENGINEERED_PRIVATE = 'UNLOCKED_ENGINEERED_PRIVATE',
  LOCKED_NATIVE_EPS_MEMBER = 'LOCKED_NATIVE_EPS_MEMBER'
}

export type OfferRestrictionType = OFFER_RESTRICTION_TYPES

/**
 *
 * Receives an {@link AugmentedOffer} and a `boolean` as parameters and returns the {@link OfferRestrictionType}
 *
 * @example
 *
 * ```
 * getOfferRestrictionType(offer, isPrivileged)
 * ```
 *
 * @param offer - The offer to be checked
 * @param isPrivileged - Whether the user has privileges or not
 *
 * @returns An {@link OfferRestrictionType} string with offer restriction type
 */
export const getOfferRestrictionType = (
  offer: AugmentedOffer,
  isPrivileged?: boolean
): OfferRestrictionType => {
  const privateDeal = isPrivateDeal(offer)
  const lockedDeal = isLockedDeal(offer, isPrivileged)
  const nativeProvider = isBofhOffer(offer)
  const isEpsMemberDeal = isEpsProvider(offer.providerCode)

  const OT = OFFER_RESTRICTION_TYPES

  if (not(privateDeal)) {
    return OT.UNRESTRICTED
  }

  if ([lockedDeal, isEpsMemberDeal, nativeProvider].every(Boolean)) {
    return OT.LOCKED_NATIVE_EPS_MEMBER
  }

  if ([lockedDeal, nativeProvider].every(Boolean)) {
    return OT.LOCKED_NATIVE_PRIVATE
  }

  if ([lockedDeal, not(nativeProvider)].every(Boolean)) {
    return OT.LOCKED_ENGINEERED_PRIVATE
  }

  if ([not(lockedDeal), nativeProvider].every(Boolean)) {
    return OT.UNLOCKED_NATIVE_PRIVATE
  }

  if ([not(lockedDeal), not(nativeProvider)].every(Boolean)) {
    return OT.UNLOCKED_ENGINEERED_PRIVATE
  }
}

/**
 *
 * Receives an {@link AugmentedOffer} or an {@link Offer} as parameter and determines if is a Closed User Group (CUG) offer
 *
 * @example
 *
 * ```
 * isPrivateDeal(offer)
 * ```
 *
 * @param offer - The offer to be checked
 *
 * @returns Whether offer is Closed User Group (CUG) offer or not
 */
export const isPrivateDeal: IsPrivateDealType = offer =>
  includes(head(offer?.cug || []), [
    OFFLINE_CUG_TYPE,
    SIGNED_IN_CUG_TYPE,
    SENSITIVE_CUG_TYPE,
    PLUS_CUG_TYPE,
    FRIENDS_AND_FAMILY_CUG_TYPE
  ])

export const isMemberPlusDeal: IsPrivateDealType = offer =>
  includes(head(offer.cug || []), [PLUS_CUG_TYPE, FRIENDS_AND_FAMILY_CUG_TYPE])

/**
 *
 * Receives an {@link AugmentedOffer} or an {@link Offer} as parameter and determines if is Plus Deal
 *
 * @example
 *
 * ```
 * isPlusDeal(offer)
 * ```
 *
 * @param offer - The offer to be checked
 *
 * @returns Whether offer is Plus Deal
 */
export const isPlusDeal: IsPrivateDealType = offer => {
  const isPrivateDealOffer = isPrivateDeal(offer)
  const isMemberPlusDealOffer = isMemberPlusDeal(offer)
  const userTierFromCookie = toggle(
    'f54ef0b3-cognito-auth',
    getUserTierFromCookie(),
    getCognitoUserTierFromCookie()
  ) as USER_TIERS[] | []
  const isMemberPlusUser = hasPlusTier(userTierFromCookie)
  return isMemberPlusDealOffer || (isPrivateDealOffer && isMemberPlusUser)
}

/**
 *
 * Receives an {@link AugmentedOffer} and a `boolean` as parameters and determines if the offer should appear locked on the website
 *
 * @example
 *
 * ```
 * isLockedDeal(offer, isPrivileged)
 * ```
 *
 * @param offer - The offer to be checked
 * @param isPrivileged - Whether the user has privileges or not
 *
 * @returns Whether the offer should appear locked on the website or not
 */
export const isLockedDeal: IsLockedDealType = (offer, isPrivileged) =>
  isPrivateDeal(offer) && !isPrivileged

/**
 *
 * Receives an {@link AugmentedOffer} and a `boolean` as parameters and determines if is an EPS member deal
 *
 * @example
 *
 * ```
 * isOfferEpsMemberDeal(offer, isPrivileged)
 * ```
 *
 * @param offer - The offer to be checked
 * @param isPrivileged - Whether the user has privileges or not
 *
 * @returns Whether the offer should appear as EPS member deal
 */
export const isOfferEpsMemberDeal: IsLockedDealType = pipe<
  [AugmentedOffer, boolean],
  OFFER_RESTRICTION_TYPES,
  boolean
>(
  curry(getOfferRestrictionType),
  equals(OFFER_RESTRICTION_TYPES.LOCKED_NATIVE_EPS_MEMBER)
)

/**
 *
 * Receives an {@link AugmentedOffer} as parameter and determines if FindHotel is the provider for that rate.
 *
 * @example
 *
 * ```
 * getProviderCodeForOfferRestrictionType(offer, isAuthenticated)
 * ```
 *
 * @param offer - The offer to be checked
 * @param isAuthenticated - Whether the user is authenticated or not
 *
 * @returns The provider code for a given offer
 */
export const getProviderCodeForOfferRestrictionType = (
  offer: AugmentedOffer,
  isAuthenticated: boolean
): string =>
  allPass([isPrivateDeal, isLockedDeal])(offer, isAuthenticated)
    ? DEFAULT_NATIVE_PRIVATE_DEAL_PROVIDER_CODE
    : offer.providerCode

/**
 *
 * Determines if deal is EngineeredPrivateDeal.
 *
 * @example
 *
 * ```
 * getIsEngineeredPrivateDeal(offer, brand, isPrivileged)
 * ```
 *
 * @param offer - The offer to be checked
 * @param isPrivileged - Whether the user has privileges or not
 *
 * @returns Whether offer is EngineeredPrivateDeal
 */
export const getIsEngineeredPrivateDeal = (
  offer: AugmentedOffer,
  isPrivileged: boolean
): boolean => {
  const isLockedEngineeredPrivateDeal =
    getOfferRestrictionType(offer, isPrivileged) ===
    OFFER_RESTRICTION_TYPES.LOCKED_ENGINEERED_PRIVATE

  return isLockedEngineeredPrivateDeal
}

/**
 *
 * Checks if the audience token is stored at local storage
 *
 * @returns When the `audienceToken` exists on the local storage
 */
export const hasAudienceTokenInBrowser = (): boolean =>
  Boolean(
    safelyGetStorageItem(localStorage, 'audienceToken', 'audienceToken failed')
  )

/**
 *
 * Receives the URL params as an object. The params may contain a token property. The function determines if the token is equals to the {@link SIGN_UP_TOKEN}
 *
 * @example
 *
 * ```
 * hasAudienceTokenInUrl({ token })
 * ```
 *
 * @param urlParams - An object that may contain a token property
 *
 * @returns Whether the token is equal to the {@link SIGN_UP_TOKEN}
 */
export const hasAudienceTokenInUrl = ({
  token
}: {
  token?: PrivilegedUserTokenType
}): boolean => equals(token, SIGN_UP_TOKEN)

/**
 * Receives the URL params as an object. The params may contain a `gsite` property. The function finds the `gsite` param
 * from anywhere in the params object and returns its value.
 *
 * @example
 *
 * ```
 * getGsiteValueFromUrl(urlParams)
 * ```
 *
 * @param urlParams URL param object
 */
export const getGsiteValueFromUrl = (
  urlParams: SearchParamsType = {}
): string | undefined => {
  /**
   * The following regex matches the `gsite` until an `&` occurs. For example: roomId=123&gsite=testValue&placeId=456
   * It will match `gsite=testValue`, and the return value of the function would be `testValue`
   */
  const gsiteRegex = /gsite=([^&]*)/
  const decodedURLParams =
    urlParams &&
    (Object.entries(urlParams).reduce(
      (acc, [key, value]) => ({
        ...acc,
        [key]: decodeURIComponent(value)
      }),
      {}
    ) as Record<string, string>)
  const url = new URLSearchParams(decodedURLParams).toString()
  return gsiteRegex.exec(decodeURIComponent(url))?.[1]
}

/**
 *
 * Receives the URL params as an object. The function use Regex to find the gsite param anywhere in the URL and
 * determines if the gsite is matches to the {@link GSITE_VALUE_REGEX}
 *
 * @example
 *
 * ```
 * hasExpectedGsiteValueInUrl(urlParam)
 * ```
 *
 * @param urlParams URL params object
 *
 * @returns Whether the gsite param matches to the {@link GSITE_VALUE_REGEX}
 */
export const hasExpectedGsiteValueInUrl = (
  urlParams: SearchParamsType
): boolean => {
  const gsite = getGsiteValueFromUrl(urlParams)
  return Boolean(gsite) && Boolean(gsite.match(GSITE_VALUE_REGEX))
}

/**
 * Checks if offer savings should be hidden
 *
 * @param offer - the {@link AugmentedOffer} to be checked
 *
 * @returns Whether offer savings should be hidden
 */
export const getShouldHideSavings = (offer: AugmentedOffer): boolean => {
  const isPrivate = isPrivateDeal(offer) || isMemberPlusDeal(offer)
  const nativeProvider = isBofhOffer(offer)
  if (nativeProvider) return !isPrivate
  return true
}

/**
 * Checks if offer price should be hidden
 *
 * @param offer - the {@link AugmentedOffer} to be checked
 * @param isPrivileged - Whether the user has privileges or not
 *
 * @returns Whether price should be hidden
 */
export const getShouldHidePrice = (
  offer: AugmentedOffer,
  isPrivileged: boolean
): boolean => {
  const offerRestrictionType = getOfferRestrictionType(offer, isPrivileged)
  return any(equals(offerRestrictionType))([
    OFFER_RESTRICTION_TYPES.LOCKED_NATIVE_PRIVATE,
    OFFER_RESTRICTION_TYPES.LOCKED_NATIVE_EPS_MEMBER
  ])
}

/**
 * Checks if any offer has private deal
 * @param offers - the {@link AugmentedOffer} to be checked
 * @returns whether the offers have or not private deals
 */
export const offersHasPrivateDeal = (
  offers: SapiOffer[] | AugmentedOffer[] | Offer[]
) => Boolean(offers?.some(offer => isPrivateDeal(offer)))

/**
 * Checks if any offer has member plus deal
 * @param offers - the {@link AugmentedOffer} to be checked
 * @returns whether the offers have or not member plus deals
 */
export const offersHasMemberPlusDeal = (
  offers: SapiOffer[] | AugmentedOffer[] | Offer[]
) => Boolean(offers?.some(offer => isMemberPlusDeal(offer)))

/**
 * Offers sorting compare function.
 * Move Member deals to the top.
 *
 * @param a
 * @param b
 */
export const memberDealOfferSorter = (a, b) => {
  if (isPrivateDeal(a) && !isPrivateDeal(b)) return -1
  if (isPrivateDeal(b) && !isPrivateDeal(a)) return 1
  return 0
}
